import { lazy, PropsWithChildren, Suspense } from 'react';
import styled from '@emotion/styled';
import { Header, Footer } from '@myrealtrip/layout-ui';
import { media } from '@myrealtrip/modash';

import { TARGET_ENV } from '~config/publicEnv';
import { useMemberHash } from '~queries/channelTalk/useMemberHash';
import { useMember } from '~queries/member/useMember';
import { useVerticalItems } from '~queries/discovery/useVerticalItems';
import { useMounted } from '~hooks/useMounted';
import { DESKTOP_PRIMARY_CONTAINER_WIDTH, MRT_FOOTER_ID } from '~constants/common';

import ErrorBoundary from './ErrorBoundary';
import dynamic from 'next/dynamic';
import { useWebView } from '@myrealtrip/device-detect';

const Widget = typeof window === 'undefined' ? () => null : lazy(() => import('remote/CsWidget'));
const LiveCommerceFloatingButton = dynamic(() => import('remote/LiveCommerce'), {
  ssr: false,
  loading: () => null,
});

interface Props {
  className?: string;
  hideHeader?: boolean;
  hideFooter?: boolean;
  enableChannelIO?: boolean;
  isFullWidth?: boolean;
  maxWidth?: number;
}

export default function CommonLayout({
  className,
  hideHeader = false,
  hideFooter = true,
  isFullWidth = false,
  maxWidth = DESKTOP_PRIMARY_CONTAINER_WIDTH,
  children,
}: PropsWithChildren<Props>) {
  const isInitWidget = useMounted();
  const { isWebView } = useWebView();

  const { data } = useMember();
  const { data: memberHashData } = useMemberHash();
  const { data: verticalItems } = useVerticalItems();
  const { id, name, emailAddress, activeCouponCodeCount, point, phoneNumber, profileImageUrl } =
    data ?? {};
  const {
    vertical_items: mainVerticalItems = [],
    sub_vertical_items: subVerticalItems = [],
    lnb_vertical_items: lnbVerticalItems = [],
  } = verticalItems ?? {};
  const user = {
    id: id ?? 0,
    hashMemberId: memberHashData?.memberHash ?? '',
    username: name ?? '',
    email: emailAddress ?? '',
    phoneNumber: phoneNumber ?? '',
    point: point ?? 0,
    numberOfCoupons: activeCouponCodeCount ?? 0,
    profileImage: profileImageUrl ?? '',
  };

  return (
    <>
      <FrontHeaderStyleCover isHide={hideHeader}>
        <Header
          theme="white"
          size="default"
          user={user}
          mainVerticalItems={mainVerticalItems}
          subVerticalItems={subVerticalItems}
          lnbVerticalItems={lnbVerticalItems}
          targetEnv={TARGET_ENV}
          isDisabled={false}
        />
      </FrontHeaderStyleCover>
      <Layout isFullWidth={isFullWidth} maxWidth={maxWidth} className={className}>
        <>{children}</>
      </Layout>
      <FrontFooterStyleCover isHide={hideFooter} id={MRT_FOOTER_ID}>
        <Footer targetEnv={TARGET_ENV} />
      </FrontFooterStyleCover>
      {isInitWidget && (
        <ErrorBoundary fallback={null} name="widget">
          <Suspense>
            <Widget user={user} />
          </Suspense>
        </ErrorBoundary>
      )}
      {isInitWidget && !isWebView && (
        <ErrorBoundary fallback={null} name="livecommerce">
          <Suspense>
            <LiveCommerceFloatingButton />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
}

const FrontHeaderStyleCover = styled.div<{
  isHide?: boolean;
}>`
  display: ${({ isHide }) => (isHide ? 'none' : 'block')};
`;

const FrontFooterStyleCover = styled.div<{
  isHide?: boolean;
}>`
  display: ${({ isHide }) => (isHide ? 'none' : 'block')};
`;

const Layout = styled.main<{ isFullWidth: boolean; maxWidth: number }>`
  flex: 1;
  width: 100%;
  max-width: ${({ isFullWidth, maxWidth }) => (isFullWidth ? '100%' : `${maxWidth}px`)};
  margin: 0 auto;
  background-color: ${({ theme }) => theme.colors.white};

  ${media.mobile} {
    width: 100%;
    margin: 0;
  }
`;
